import React, { useEffect, useMemo, useRef } from 'react';
import Message from './Message';
import WelcomeScreen from './WelcomeScreen';
import { sanitizeSelectionForClipboard } from '../utils/clipboardSanitize';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../i18n';

// Lightweight shimmer placeholder shown while a conversation with no warm buffer
// is being fetched (Fire-and-Forget "no white screen" guarantee). It occupies the
// message area with a few pulsing bubble outlines so a cold switch never flashes a
// blank/white panel or the WelcomeScreen before the real messages arrive.
const ChatSkeleton = () => (
    <div data-testid="chat-skeleton" className="py-4 px-4 space-y-4" aria-hidden="true">
        {[...Array(4)].map((_, i) => (
            <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                <div className="flex items-start gap-2 max-w-[70%]">
                    {i % 2 === 0 && (
                        <div className="w-8 h-8 rounded-full bg-white/[0.07] animate-pulse shrink-0" />
                    )}
                    <div
                        className="h-14 rounded-2xl bg-white/[0.07] animate-pulse"
                        style={{ width: `${180 + (i * 37) % 140}px` }}
                    />
                    {i % 2 !== 0 && (
                        <div className="w-8 h-8 rounded-full bg-white/[0.07] animate-pulse shrink-0" />
                    )}
                </div>
            </div>
        ))}
    </div>
);

const ChatMessages = ({
    messages,
    attachedFiles,
    messagesEndRef,
    highlightedMessageId,
    conversationId,
    onEditMessage,
    onJumpToMessage,
    onReply,
    replyingToMessages = [],
    isReadOnly = false,
    searchQuery = '',
    userInitials = 'U',
    userName = null,
    isLoadingConversation = false,
    // #439: re-send a failed turn (Main Office only); `retryDisabled` while the
    // conversation is already processing.
    onRetry = null,
    retryDisabled = false,
    // #448 re-open: the last message is a user message the server is still
    // answering (or that never got an answer). `{ status: 'waiting' | 'sending'
    // | 'stale', onResend }` — "PAI is working…" while a stamped run is going,
    // "Sending…" while an unstarted turn is re-sent, then a Resend button.
    pendingTurn = null,
}) => {
    const { t } = useLanguage();
    // Hooks must run unconditionally (rules-of-hooks) — these were previously called
    // AFTER the empty-state early return below, so the hook count changed when a
    // conversation went empty↔non-empty (React "rendered more hooks than during the
    // previous render" crash). Both computations are pure and cheap and degrade to an
    // empty Set / empty object when there are no messages, so hoisting is behavior-safe.

    // F-5.1: derive the set of reply-selected ids ONCE per render, then pass each
    // Message a PRIMITIVE boolean (`isSelectedForReply`). Passing the Set directly
    // would defeat memo(Message) — a fresh Set every render fails identity checks —
    // and threading the primitive keeps the comparator cheap while preserving the
    // reply-selection highlight (the Set itself is intentionally NOT compared in
    // arePropsEqual).
    const selectedReplyIds = useMemo(
        () => new Set(replyingToMessages.map(m => m.dbId || m.id)),
        [replyingToMessages]
    );

    // Dark Mode Copy-Paste Formatting (#345): the browser's native copy
    // handler bakes the COMPUTED style of the selection (dark bubble bg,
    // light text, forced link color/underline) into the clipboard's
    // text/html flavor, so a reply copied out of the dark PAI theme carries
    // those artifacts into Gmail/Word/Outlook. Intercepting `copy` at the
    // DOCUMENT level (a plain-text selection dispatches the event on
    // document.activeElement — usually <body> — never on this component's
    // own subtree, so a listener on the container itself would never see
    // it) and rebuilding the clipboard payload ourselves is the only way to
    // stop the native writer from running. Scoped to selections that fall
    // INSIDE this messages container via `containerRef`, so the composer,
    // sidebar, and every other panel are completely unaffected. Both themes
    // produce byte-identical output because the sanitizer never emits a
    // style/class attribute in the first place — see clipboardSanitize.js.
    const containerRef = useRef(null);
    useEffect(() => {
        const handleCopy = (event) => {
            const container = containerRef.current;
            if (!container) return;
            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;
            const range = selection.getRangeAt(0);
            if (!container.contains(range.commonAncestorContainer)) return;
            const payload = sanitizeSelectionForClipboard(selection);
            if (!payload) return;
            event.clipboardData.setData('text/plain', payload.text);
            event.clipboardData.setData('text/html', payload.html);
            event.preventDefault();
        };
        document.addEventListener('copy', handleCopy);
        return () => document.removeEventListener('copy', handleCopy);
    }, []);

    // Find-in-conversation Bug #352: the per-message cumulative match offset that
    // used to be computed here is GONE. It counted hits in the RAW markdown source
    // while the highlights are emitted from the RENDERED text, so the two numberings
    // drifted — the counter promised an "N of M" whose Nth element often did not
    // exist. Numbering is now assigned after render by walking the real marks in
    // document order (`useInChatSearch`), which is both the count the user sees and
    // the thing the arrows can actually scroll to.

    if (messages.length === 0 && attachedFiles.length === 0) {
        // A cold conversation switch (no warm buffer yet) shows the skeleton, not
        // the WelcomeScreen — the welcome only belongs to a genuinely new/empty chat.
        if (isLoadingConversation) return <ChatSkeleton />;
        return <WelcomeScreen userName={userName} />;
    }

    // Find the last user message ID
    const lastUserMessageId = [...messages].reverse().find(m => m.isUser)?.id;

    // #457 Hybrid Substitution: a "PAI was stopped" notice is replaced by the
    // next reply. It is HIDDEN (never deleted — it stays in the database as the
    // honest record) once any later assistant message exists, including the
    // live bubble of a new turn. With nothing after it, it stays the final entry.
    const visibleMessages = messages.filter((m, i) => {
        if (m.isUser || !m.metadata?.stopped) return true;
        return !messages.slice(i + 1).some(later => !later.isUser);
    });

    // New UI #98: chat-app-style bubble layout — no row dividers; each Message
    // renders a left/right-aligned bubble with its own vertical spacing.
    return (
        <div ref={containerRef} data-testid="chat-messages" className="py-2">
            {visibleMessages.map((message) => (
                <Message
                    key={message.id}
                    message={message}
                    isUser={message.isUser}
                    isHighlighted={highlightedMessageId === message.id}
                    conversationId={conversationId}
                    isLastUserMessage={message.id === lastUserMessageId}
                    onEditMessage={onEditMessage}
                    onJumpToMessage={onJumpToMessage}
                    onReply={onReply}
                    isSelectedForReply={selectedReplyIds.has(message.dbId || message.id)}
                    isReadOnly={isReadOnly}
                    searchQuery={searchQuery}
                    userInitials={userInitials}
                    onRetry={onRetry}
                    retryDisabled={retryDisabled}
                />
            ))}
            {pendingTurn && (
                <div
                    data-testid="turn-pending-placeholder"
                    data-status={pendingTurn.status}
                    className="flex items-center gap-2 px-4 py-3 text-[13px] text-pai-secondary"
                >
                    {pendingTurn.status === 'stale' ? (
                        <>
                            <span>{t('msgTurnPendingStale', 'No reply arrived for this message.')}</span>
                            {pendingTurn.onResend && (
                                <button
                                    type="button"
                                    data-testid="turn-pending-retry"
                                    data-action="resend"
                                    onClick={pendingTurn.onResend}
                                    className="pai-tap-row text-blue-400 hover:text-blue-300 hover:underline"
                                >
                                    {t('msgTurnResend', 'Resend')}
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            <Loader2 size={14} className="animate-spin shrink-0" />
                            <span>{pendingTurn.status === 'sending'
                                ? t('msgTurnSending', 'Sending…')
                                : t('msgTurnPending', 'PAI is working on this…')}</span>
                        </>
                    )}
                </div>
            )}
            <div ref={messagesEndRef} />
        </div>
    );
};

export default ChatMessages;